import React from "react";
import { motion } from "framer-motion";
import { benefitsSection } from "../data/mockData";
import { CheckCircle2 } from "lucide-react";

const BenefitsSection = () => {
  return (
    <section className="bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Divider line between sections */}
      <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="border-t border-gray-200" />
      </div>

      {/* Benefits Section Content */}
      <div className="py-16 md:py-24 lg:py-28">
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-24">
          {/* Heading - Two column layout matching Figma */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-20">
            <div>
              <h2 
                className="text-[#1a1a2e] leading-[1.1]"
                style={{ fontSize: "clamp(28px, 4.5vw, 52px)", fontWeight: 800 }}
              >
                Real Benefits That Go Beyond Learning
              </h2>
            </div>
            <div className="flex items-center">
              <p className="text-gray-500 text-[15px] sm:text-[16px] md:text-[17px] leading-relaxed">
                From building future-ready talent to improving business productivity, Elevante enables outcomes that matter.
              </p>
            </div>
          </div>

          {/* Benefits Cards */}
          <div className="space-y-24">
            {benefitsSection.features.map((feature, idx) => {
              // Text always on left, image always on right
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: idx * 0.05, ease: "easeOut" }}
                  className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-16 items-end"
                >
                  {/* Left: Text content (narrower) - bottom aligned */}
                  <div className="flex flex-col justify-end">
                    <h3 className="text-[26px] md:text-[32px] lg:text-[36px] font-bold text-[#1a1a2e] mb-5 leading-tight">
                      {feature.title}
                    </h3>
                    <p className="text-[14px] md:text-[15px] text-gray-600 leading-relaxed mb-6">
                      {feature.description}
                    </p>
                    <div className="space-y-3.5">
                      {feature.bullets.map((bullet, bi) => (
                        <div key={bi} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span 
                            className="text-[13px] md:text-[14px] text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: bullet }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Screenshot image (wider) */}
                  <div className="w-full">
                    <div className="group rounded-xl overflow-hidden shadow-lg border border-gray-200 transition-all duration-500 ease-out hover:shadow-[0_12px_36px_rgba(15,15,30,0.12)]">
                      <img 
                        src={feature.imageUrl} 
                        alt={feature.title}
                        className="w-full h-auto object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
